// OEM App 定制 — 资料清单配置
// 数据源：OEMApp定制—客户前期资料清单.md（4 类：Android/iOS × 国内/海外）
//         OEMApp定制—内部研发两阶段客户资料清单.md（开发一阶段 / 开发二阶段）
// TODO: 联调时替换为接口数据
import { CN_APP_STORES } from './platformStores';

// ── 维度选项 ──
export const PLATFORM_OPTIONS = [
    { label: 'Android', value: 'android' },
    { label: 'iOS', value: 'ios' },
];
export const REGION_OPTIONS = [
    { label: '国内', value: 'cn' },
    { label: '海外', value: 'os' },
];
// iOS 上架区域为单选：全球通用 / 国内 / 海外（三选项所需资料一致，统一按全球通用一套）
export const IOS_REGION_OPTIONS = [
    { label: '全球通用', value: 'global' },
    { label: '国内', value: 'cn' },
    { label: '海外', value: 'os' },
];
export const PLATFORM_LABEL = { android: 'Android', ios: 'iOS' };
export const REGION_LABEL = { cn: '国内', os: '海外', global: '全球通用' };

// ── 阶段定义（仅两个开发阶段）──
export const STAGES = [
    { key: 'phase1', title: '开发一阶段' },
    { key: 'phase2', title: '开发二阶段' },
];

// 资料项提交形态：text=文本填写，file=文件上传，color=取色器，platform-push=平台配置
// extra 可附加 kind:'image'（图片上传，展示缩略图）等
const item = (name, desc, mode = 'text', extra = {}) => ({ name, desc, mode, ...extra });
// 渠道配置项：一个卡片内含多个渠道，左侧渠道列表 + 右侧字段（抽屉内配置）
const channelItem = (name, desc, channels, extra = {}) => ({ name, desc, mode: 'channels', channels, ...extra });

// 给资料项补稳定 key
const withKeys = (sectionKey, items) => items.map((it, i) => ({ ...it, key: `${sectionKey}-${i}` }));

/**
 * 按「平台 + 上架区域」关联配置动态生成资料清单。
 * targets: [{ platform: 'android'|'ios', regions: ['cn'|'os', ...] }]
 * 返回：[{ key, title, sections: [{ key, title, items }] }]，仅开发一/二阶段
 */
export const buildChecklist = (targets = []) => {
    const list = Array.isArray(targets) ? targets : [];
    const aR = list.find((t) => t.platform === 'android')?.regions || [];
    const iosTarget = list.find((t) => t.platform === 'ios');
    const hasI = !!iosTarget;
    const iosRaw = iosTarget?.regions;
    const iosRegions = Array.isArray(iosRaw) ? iosRaw : (iosRaw ? [iosRaw] : []);
    const aCn = aR.includes('cn');
    const aOs = aR.includes('os');
    // iOS 区域单选：国内→中文/国内项；海外→英文/海外项；全球通用→两者并集
    const iCn = hasI && (iosRegions.includes('cn') || iosRegions.includes('global'));
    const iOs = hasI && (iosRegions.includes('os') || iosRegions.includes('global'));
    const cn = aCn || iCn;
    const os = aOs || iOs;

    // ══════════ 开发一阶段 ══════════
    const entity = [item('公司营业执照', '上传营业执照扫描件，需在有效期内', 'file')];
    if (cn) entity.push(item('公司名称（中文）', '客户公司中文全称'));
    if (os) entity.push(item('公司名称（英文）', '客户公司英文全称（用于海外上架和软著）'));
    entity.push(item('客服邮箱', 'App 内用户支持邮箱，商店审核会校验有效性'));
    entity.push(item('官网地址', cn ? '官网 URL；国内为 ICP 备案前置条件' : '客户官方网站 URL'));

    const spec = [];
    if (cn) spec.push(item('应用名称（中文）', '国内商店展示名称，需与软著一致'));
    if (os) spec.push(item('应用名称（英文）', '海外商店展示名称，需与软著一致'));
    if (aCn) spec.push(item('Android 包名（国内）', '格式 com.应用名.公司名，不能含 carecam'));
    if (aOs) spec.push(item('Android 包名（海外）', '格式 com.应用名.公司名，不能含 carecam'));
    if (hasI) spec.push(item('iOS 包名（Bundle ID）', '格式 com.应用名.公司名，不能含 carecam'));
    spec.push(item('包装二维码链接', '彩盒包装上下载 App 的二维码链接'));
    if (cn) spec.push(item('应用 Logo（国内）', 'PNG/SVG，1024×1024px 以上，用于国内应用商店，一经锁定不可更改', 'file', { kind: 'image' }));
    if (os) spec.push(item('应用 Logo（海外）', 'PNG/SVG，1024×1024px 以上，用于海外应用商店，一经锁定不可更改', 'file', { kind: 'image' }));
    if (cn) spec.push(item('应用简介（中文）', '功能描述、一句话简介、详细介绍、关键词、简短介绍（80 字符）、详细描述（1000/400 字符）'));
    if (os) spec.push(item('应用简介（英文）', '海外商店展示：功能描述、一句话简介、详细介绍、关键词、简短介绍（80-180 字符）、详细描述（4000 字符以内）'));
    spec.push(item('主题颜色', 'App UI 主题色值（基于平台换肤）', 'color'));

    const content = [];
    if (cn) content.push(
        item('客服电话（国内）', 'App 国内客服电话'),
        item('客服邮箱（国内）', 'App 国内客服邮箱'),
        item('在线客服入口（国内）', '国内在线客服链接 / 入口'),
    );
    if (os) content.push(
        item('客服电话（海外）', 'App 海外客服电话'),
        item('客服邮箱（海外）', 'App 海外客服邮箱'),
        item('在线客服入口（海外）', '海外在线客服链接 / 入口'),
    );
    if (cn) content.push(item('版权声明（中文）', 'App 版权信息（中文）'));
    if (os) content.push(item('版权声明（英文）', 'App 版权信息（英文）'));

    // ══════════ 开发一阶段 ══════════
    const loginChannels = [];
    if (cn) loginChannels.push(
        { key: 'wechat', name: '微信登录', fields: ['APPID', 'App Secret', 'App Universal Link（iOS）'] },
        { key: 'mobtech', name: 'MobTech 短信', fields: ['App Key', 'App Secret'] },
        { key: 'tencent', name: '腾讯短信', fields: ['App Key', 'App Secret'] },
        { key: 'ali', name: '阿里短信', fields: ['App Key', 'App Secret'] },
    );
    if (os) loginChannels.push({ key: 'email', name: '邮箱', fields: [{ name: '邮箱服务商', type: 'select', options: ['阿里邮箱', '腾讯邮箱'] }, '接收邮箱', 'accessKeyId', 'accessKeySecret'] });

    const phase1Sections = [
        { key: 'p1-entity', title: '企业主体信息', items: withKeys('p1-entity', entity) },
        { key: 'p1-spec', title: 'APP 规格信息', items: withKeys('p1-spec', spec) },
        { key: 'p1-login', title: '用户登录', items: withKeys('p1-login', [channelItem('用户登录', '各登录方式的参数配置（可填写或上传）', loginChannels)]) },
    ];

    // ══════════ 开发二阶段 ══════════
    const push = [];
    if (aCn) push.push(channelItem(
        '上架平台&推送',
        '勾选要上架的应用商店，并配置各自的推送 / 开发者账号参数；应用宝、百度无推送',
        CN_APP_STORES.map((s) => ({ key: s.key, name: s.name, push: s.push, fields: s.fields })),
        { selectable: true, manageLabel: '管理上架平台', selectHint: '勾选要上架的平台' },
    ));
    if (aOs) push.push(channelItem('Google 推送', '配置 Google 推送各参数', [
        { key: 'google', name: 'Google 推送', fields: ['项目 ID', 'Sender ID', '服务器秘钥（Server Key）', { name: 'google-services.json', type: 'file', required: true }, 'Web 客户端 ID', 'Android Client ID', 'iOS Client ID', 'Web Client ID', { name: 'GoogleService-Info.plist', type: 'file' }] },
    ]));

    const certs = [];
    if (hasI) certs.push(
        item('iOS 开发证书', 'Mobileprovision 证书（.mobileprovision）、p12 证书（需证书 + 证书密码）', 'file'),
        item('iOS 发布证书', 'Mobileprovision 证书、p12 证书（需证书 + 证书密码）', 'file'),
        item('iOS 推送证书', '生产环境证书文件（.p12，需证书 + 证书密码）', 'file'),
    );

    const payChannels = [];
    if (cn) payChannels.push(
        { key: 'wechat', name: '微信支付', fields: ['商户 ID', 'appID', 'apiV2Key', '订阅计划 ID', '沙盒账号'] },
        { key: 'alipay', name: '支付宝', fields: ['APPID', '公钥', '私钥', '沙盒账号'] },
    );
    if (os) payChannels.push(
        { key: 'applepay', name: 'Apple Pay', fields: ['apiV2Key', '客户邮箱', { name: '秘钥 P12 文件', type: 'file' }, '应用接口密钥', '沙盒账号'] },
        { key: 'googlepay', name: 'Google Pay', fields: ['客户邮箱', { name: '秘钥 P12 文件', type: 'file' }, '应用接口密钥', '沙盒账号'] },
        { key: 'paypal', name: 'PayPal', fields: ['客户 ID', '客户私钥', '沙盒账号'] },
        { key: 'stripe', name: 'Stripe', fields: ['公钥', '密钥', '沙盒账号'] },
        { key: 'airwallex', name: '空中云汇', fields: ['客户 ID', 'API 密钥', '沙盒账号'] },
    );

    const platformCfg = [];
    if (aOs) platformCfg.push(item('Google 套餐内购key', 'Google key'));
    if (hasI) platformCfg.push(item('iOS 套餐内购key', 'iOS key'));

    const phase2Sections = [];
    if (push.length) phase2Sections.push({ key: 'p2-push', title: '上架平台&推送', items: withKeys('p2-push', push) });
    if (certs.length) phase2Sections.push({ key: 'p2-cert', title: '证书', items: withKeys('p2-cert', certs) });
    if (payChannels.length) phase2Sections.push({
        key: 'p2-pay',
        title: '支付',
        items: withKeys('p2-pay', [
            channelItem('支付渠道', '勾选要启用的支付渠道，并配置参数；可同步到企业商户号', payChannels, { selectable: true, manageLabel: '管理支付渠道', selectHint: '勾选要启用的支付渠道', syncable: true }),
            item('一键导入支付', '将二阶段已配置的支付渠道一键导入企业商户号', 'import'),
        ]),
    });
    phase2Sections.push({ key: 'p2-platform', title: '内购套餐配置', items: withKeys('p2-platform', platformCfg) });
    if (content.length) phase2Sections.push({ key: 'p2-content', title: '内容素材类', items: withKeys('p2-content', content) });

    return [
        { key: 'phase1', title: '开发一阶段', sections: phase1Sections },
        { key: 'phase2', title: '开发二阶段', sections: phase2Sections },
    ];
};

// 资料项状态（仅保存，无审核流程）
export const ITEM_STATUS = {
    pending: { text: '待填写', color: 'default' },
    draft: { text: '已保存', color: 'success' },
};
